import React from 'react';
import {
  Dialog,
  Fact,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TranscriptView,
} from '../../ui/v2';
import {
  adminCallProductLabel,
  dollarsToCentsInt,
  formatCostDuration,
  parseAdminCallTranscript,
} from './adminCallsModel';

export type AdminCallDialogType = 'transcript' | 'summary' | 'cost';

const CallFact: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => <Fact label={label} value={value || '—'} missing={!value} />;

const AdminCallDialogs: React.FC<{
  call: any | null;
  type: AdminCallDialogType | null;
  onClose: () => void;
}> = ({ call, type, onClose }) => {
  const cost = call?.call_cost;
  return (
    <>
      <Dialog
        open={type === 'transcript' && !!call}
        onClose={onClose}
        title="Call Transcript"
        size="lg"
      >
        {call && (
          <>
            <dl className="grid grid-cols-2 gap-3 border-y border-v2-line py-3">
              <CallFact label="Call ID" value={call.call_id} />
              <CallFact label="Customer" value={call.customer_name} />
              <CallFact label="Store" value={call.store_name} />
              <CallFact label="Direction" value={call.direction} />
            </dl>
            <div
              role="region"
              aria-label="Transcript"
              tabIndex={0}
              className="v2-scroll mt-4 max-h-[60vh] overflow-y-auto pr-2"
            >
              {call.transcript ? (
                <TranscriptView
                  lines={parseAdminCallTranscript(call.transcript)}
                  guestLabel={call.customer_name?.split(' ')[0] || 'Caller'}
                />
              ) : (
                <p className="text-v2-12 text-v2-fg-3">
                  No transcript available.
                </p>
              )}
            </div>
          </>
        )}
      </Dialog>

      <Dialog
        open={type === 'summary' && !!call}
        onClose={onClose}
        title="Call Summary"
      >
        {call && (
          <>
            <dl className="grid grid-cols-2 gap-3 border-y border-v2-line py-3">
              <CallFact label="Call ID" value={call.call_id} />
              <CallFact label="Customer" value={call.customer_name} />
              <CallFact label="Store" value={call.store_name} />
              <CallFact label="Sentiment" value={call.user_sentiment} />
            </dl>
            <p className="mt-4 text-v2-14 leading-[22px] text-v2-fg">
              {call.call_summary || 'No summary available.'}
            </p>
          </>
        )}
      </Dialog>

      <Dialog
        open={type === 'cost' && !!call}
        onClose={onClose}
        title="Cost Breakdown"
        description="All monetary amounts below are in cents (integer)."
      >
        {cost && (
          <>
            <dl className="grid grid-cols-2 gap-3 border-y border-v2-line py-3">
              <CallFact label="Call ID" value={call.call_id} />
              <CallFact
                label="Duration"
                value={
                  cost.total_duration_seconds != null
                    ? formatCostDuration(cost.total_duration_seconds)
                    : null
                }
              />
            </dl>
            <div className="mt-4 flex items-center justify-between rounded-v2 bg-v2-sunken px-3 py-2.5">
              <span className="text-v2-13 font-semibold text-v2-fg">
                Combined Cost (¢)
              </span>
              <span className="v2-num text-v2-16 font-semibold text-v2-fg">
                {cost.combined_cost != null
                  ? dollarsToCentsInt(cost.combined_cost)
                  : '—'}
              </span>
            </div>
            {cost.total_duration_unit_price != null && (
              <div className="mt-2 flex items-center justify-between px-3 text-v2-13 text-v2-fg-2">
                <span>Per-minute rate (¢/min)</span>
                <span className="v2-num">
                  {dollarsToCentsInt(cost.total_duration_unit_price * 60)}
                </span>
              </div>
            )}
            {cost.product_costs && cost.product_costs.length > 0 && (
              <div className="mt-4 overflow-hidden rounded-v2 border border-v2-line">
                <Table>
                  <THead>
                    <tr>
                      <TH>Product</TH>
                      <TH align="right">Unit (¢)</TH>
                      <TH align="right">Cost (¢)</TH>
                    </tr>
                  </THead>
                  <TBody>
                    {cost.product_costs.map((pc: any, i: number) => (
                      <TR key={i}>
                        <TD>{adminCallProductLabel(pc.product)}</TD>
                        <TD num align="right" className="text-v2-fg-2">
                          {pc.unit_price != null
                            ? dollarsToCentsInt(pc.unit_price)
                            : '—'}
                        </TD>
                        <TD num align="right" className="font-medium">
                          {pc.cost != null ? dollarsToCentsInt(pc.cost) : '—'}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            )}
          </>
        )}
      </Dialog>
    </>
  );
};

export default AdminCallDialogs;
